import { useEffect, useState } from "react";
import { LuDownload, LuShare, LuX, LuRefreshCw } from "react-icons/lu";
import { applyUpdate } from "../lib/pwa";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIosSafari(): boolean {
  const ua = window.navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const webkit = /WebKit/.test(ua);
  const notChrome = !/CriOS|FxiOS|EdgiOS/.test(ua);
  return iOS && webkit && notChrome;
}

const DISMISS_KEY = "flat-install-hint-dismissed";

/**
 * Install nudge. Since push now depends on installation on iOS, an uninstalled
 * user is a silently broken user — so this is functional, not decoration.
 */
export function InstallHint() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || dismissed) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  // Android/Chrome — a real install button.
  if (deferred) {
    return (
      <Bar onDismiss={dismiss}>
        <span className="flex-1">Install Flat for notifications and a home-screen icon.</span>
        <button
          onClick={async () => {
            await deferred.prompt();
            await deferred.userChoice;
            setDeferred(null);
          }}
          className="flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-brand-700"
        >
          <LuDownload className="h-4 w-4" /> Install
        </button>
      </Bar>
    );
  }

  // iOS Safari — no install API, so show the manual steps.
  if (isIosSafari()) {
    return (
      <Bar onDismiss={dismiss}>
        <span className="flex-1">
          Install Flat: tap <LuShare className="inline h-4 w-4" /> Share, then{" "}
          <span className="font-semibold">Add to Home Screen</span>. Push needs this on iPhone.
        </span>
      </Bar>
    );
  }

  return null;
}

function Bar({ children, onDismiss }: { children: React.ReactNode; onDismiss: () => void }) {
  return (
    <div className="mb-4 flex items-center gap-3 rounded-xl bg-brand-600 px-4 py-2.5 text-sm text-white shadow-sm">
      {children}
      <button onClick={onDismiss} aria-label="Dismiss" className="shrink-0 text-white/80 hover:text-white">
        <LuX className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Shown when a new service worker is waiting. User-controlled reload. */
export function UpdateBanner({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <div className="fixed inset-x-0 bottom-16 z-50 mx-auto flex max-w-md items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-lg sm:bottom-4">
      <LuRefreshCw className="h-5 w-5 shrink-0 text-brand-600" />
      <span className="flex-1 text-sm text-slate-700">A new version is ready.</span>
      <button
        onClick={applyUpdate}
        className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700"
      >
        Reload
      </button>
    </div>
  );
}
